import { KAKAO_SDK_URL } from "./kakao.config";
import type { KakaoSdk } from "./kakao.types";
import { waitUntil } from "./waitUntil";

let sdkLoadPromise: Promise<KakaoSdk> | null = null;

const isKakaoRuntimeReady = (): boolean =>
  Boolean(typeof window !== "undefined" && window.Kakao?.init);

const appendKakaoScript = (): HTMLScriptElement => {
  const script = document.createElement("script");
  script.src = KAKAO_SDK_URL;
  script.async = true;
  document.head.appendChild(script);
  return script;
};

const waitForKakaoRuntime = async (): Promise<KakaoSdk> => {
  await waitUntil(isKakaoRuntimeReady);
  return window.Kakao as KakaoSdk;
};

export const loadKakaoSdk = (): Promise<KakaoSdk> => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Kakao SDK can only load in the browser."));
  }

  if (isKakaoRuntimeReady()) {
    return Promise.resolve(window.Kakao as KakaoSdk);
  }

  if (!sdkLoadPromise) {
    sdkLoadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${KAKAO_SDK_URL}"]`,
      );

      const handleReady = () => {
        void waitForKakaoRuntime()
          .then(resolve)
          .catch((error) => {
            sdkLoadPromise = null;
            reject(error);
          });
      };

      const handleError = () => {
        sdkLoadPromise = null;
        reject(new Error("Failed to load Kakao JavaScript SDK."));
      };

      if (existing) {
        existing.addEventListener("load", handleReady, { once: true });
        existing.addEventListener("error", handleError, { once: true });

        if (isKakaoRuntimeReady()) {
          handleReady();
        }
        return;
      }

      const script = appendKakaoScript();
      script.addEventListener("load", handleReady, { once: true });
      script.addEventListener("error", handleError, { once: true });
    });
  }

  return sdkLoadPromise;
};
